import { useCallback, useEffect, useState } from 'react';
import { NotePencil } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { cn } from '~/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Textarea } from '~/components/ui/textarea';
import { ActionBar } from '~/components/ui/action-bar';
import { useCodingSessionContext } from '~/hooks/queries/use-coding-sessions';
import { useSetCodingSessionContext } from '~/hooks/mutations/use-coding-mutations';
import { useFeatureFlag, CODING_DESIGN_DIRECTION_FLAG } from '~/hooks/queries/use-feature-flags';
import { DesignDirectionEditor, DesignDirectionEditorViewer, useDesignDirectionEditor } from './design-direction-editor';
import { CODING_CONTEXT_MAX_CHARS } from './coding-events';

/**
 * FRONT-1787: the "Project context" editor, behind the coding session's "…"
 * menu. Its own file because the dialog owns a real workflow — a load that can
 * legitimately fail, a draft that must not be clobbered by a stale read, a
 * save, a destructive clear and its undo — and the modal it used to live in is
 * already past 1,900 lines (Aikido flagged the combined function; fair).
 */

/**
 * FRONT-2382 review #11/#12: the ActionBar's warning message and its
 * "Clear context" save label are a precedence rule over TWO independent
 * dirty flags (the context box, the design section), and a rule tried from
 * only one side is not tested — the bug shipped was exactly that: once the
 * design section became ALSO dirty, both the warning and the label silently
 * fell back to the generic "Save changes" wording, hiding a destructive
 * clear behind ordinary copy. `willClear` now wins outright whenever it's
 * true, regardless of `designDirty`, which is why it is checked first.
 * Exported as its own pure function (rather than inlined JSX) so the four
 * cells of that matrix can be swept without mounting the dialog — this
 * repo's frontend suite runs under plain node, no DOM.
 */
export function contextSaveCopy({
  willClear,
  designDirty,
}: {
  willClear: boolean;
  designDirty: boolean;
}): { message: string | null; saveLabel: string } {
  if (willClear) return { message: 'Saving now removes the context', saveLabel: 'Clear context' };
  if (designDirty) return { message: null, saveLabel: 'Save changes' };
  return { message: null, saveLabel: 'Save context' };
}

/**
 * FRONT-1787: read and replace the project's CODE-CONTEXT.md.
 *
 * This text rides EVERY turn of the session's system prompt, so the copy is
 * explicit about two things people would otherwise have to guess: it applies
 * from the next run (the in-flight one already has its prompt), and it travels
 * with the PROJECT, not this conversation — a later session on the same
 * project inherits it.
 *
 * Mounted by the modal as a sibling of the "…" menu and keyed by session id;
 * see the call site for why.
 */
export function ProjectContextDialog({
  sessionId,
  open,
  onOpenChange,
}: {
  sessionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: read, isLoading, isError, error } = useCodingSessionContext(sessionId);
  const saved = read?.readable ? read.context : undefined;
  /**
   * The sandbox read failed (timeout, or an unreadable file). NOT the same as
   * an empty context: this project may hold constraints we could not see, so
   * saving would overwrite them with whatever is in the box. The panel rated
   * collapsing these two a critical data-loss path, and it was right — the
   * `isError` branch below never fired, because the route answered 200 either
   * way.
   */
  const unreadable = read !== undefined && !read.readable;
  const [draft, setDraft] = useState<string | null>(null);
  const setContext = useSetCodingSessionContext();

  // Re-seed from the server on first load, and after a save clears the
  // draft. `saved` is trustworthy at that moment because the mutation writes
  // its own result into the cache before invalidating (see
  // useSetCodingSessionContext) — without that, this effect re-seeded from
  // the pre-save value and the box visibly reverted.
  useEffect(() => {
    if (draft !== null) return;
    if (saved === undefined) return;
    setDraft(saved ?? '');
  }, [saved, draft]);

  const value = draft ?? saved ?? '';
  const dirty = draft !== null && draft.trim() !== (saved ?? '').trim() && !unreadable;
  /**
   * Is the pending save a DELETION?
   *
   * Just "dirty, and the box is empty" — no third check that stored content
   * exists, because `dirty` already proves it: it requires
   * `draft.trim() !== (saved ?? '').trim()`, so an empty draft can only be
   * dirty when `saved` is non-empty. Aikido flagged the longer form as
   * contradictory logic and was right — the extra conjunct cannot be false
   * when the first two hold, so it read as a condition that guards nothing.
   */
  const willClear = dirty && value.trim().length === 0;

  // FRONT-2382: the "Design direction" section — inspiration text, chosen
  // directives, and the staged reference list. A second, independent draft
  // sharing this dialog's ONE save flow (see handleSave below): "both calls
  // when both dirty" means the context PUT and the design PUT fire
  // independently on the same Save click, never chained. Its own query,
  // draft state and save/discard wiring live in `useDesignDirectionEditor`
  // (design-direction-editor.tsx) — this dialog only reads `dirty`/`saving`
  // off it and calls `save()`/`discard()` alongside the context half.
  const designFlagOn = useFeatureFlag(CODING_DESIGN_DIRECTION_FLAG, false);
  const designEditor = useDesignDirectionEditor(sessionId, designFlagOn);
  const designDirty = designEditor.dirty;

  /**
   * Put back a context that was just cleared. Its own callback rather than a
   * mutation nested inside a toast handler inside onSuccess — the undo path
   * is the one people reach for while worried, so it should be the easiest
   * thing in this file to follow (Aikido flagged the nesting; fair).
   */
  const restore = useCallback(
    (previous: string) => {
      setContext.mutate(
        { sessionId, context: previous },
        {
          onSuccess: () => toast.success('Project context restored'),
          onError: (err) =>
            toast.error((err as Error).message || 'Could not restore the context'),
        },
      );
    },
    [sessionId, setContext],
  );

  /**
   * A save that DELETES needs a way back, and the action bar's "Revert" is not
   * one: that discards an unsaved edit and disappears the moment the save
   * lands, so emptying the box used to destroy the context with nothing to
   * undo it. Reported live ("the revert button is gone. that's scary"). An
   * undo action on the toast keeps the happy path one click while making the
   * destructive path reversible for as long as the toast is up.
   */
  const announceCleared = useCallback(
    (previous: string) => {
      toast.success('Project context cleared', {
        description: 'EDDY will start from just the project name.',
        duration: 12000,
        action: { label: 'Undo', onClick: () => restore(previous) },
      });
    },
    [restore],
  );

  // FRONT-2382: "both calls when both dirty" — the context PUT and the
  // design PUT are independent requests, so each fires only when its OWN
  // half is dirty, and both can be in flight at once off one Save click.
  // The design half's own request/toast live in `designEditor.save()`
  // (useDesignDirectionEditor) — called here on the SAME click as the
  // context save, unconditionally: it no-ops itself when not dirty.
  const handleSave = useCallback(() => {
    if (setContext.isPending || designEditor.pending) return;
    if (dirty) {
      // Captured BEFORE the write, so the undo above has something to put back.
      const previous = (saved ?? '').trim();
      const next = value.trim();
      const clearing = next.length === 0 && previous.length > 0;
      setContext.mutate(
        { sessionId, context: next },
        {
          onSuccess: (result) => {
            setDraft(null);
            // Every clearing save returns above, and a save with nothing to
            // write cannot happen — `dirty` gates the button, and empty-over-
            // empty is not dirty. So this path always means text was stored.
            // The old `result.context ? saved : cleared` ternary had an
            // unreachable else whose only route was the case where "cleared"
            // would have been false; Aikido flagged it as contradictory logic
            // and was right.
            if (clearing) return announceCleared(previous);
            toast.success('Project context saved — EDDY uses it from his next run');
          },
          onError: (err) =>
            toast.error((err as Error).message || 'Could not save the project context'),
        },
      );
    }
    designEditor.save();
  }, [sessionId, value, saved, setContext, announceCleared, dirty, designEditor]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Feedback round 2 (live finding): with a picked web suggestion in the
        reference list (it carries a description), this dialog's rows used
        to render past a 448px card and off the right edge of the viewport at
        1280×720 — cause was DialogContent's own top-level `display:grid`:
        each of ITS direct children is a grid item, which defaults to
        `min-width:auto`, so a long, un-clamped description several levels
        down could still inflate the outer track. Fixed two ways at once:
        every direct child below carries `min-w-0` (this bullet), AND the
        description that caused it now clamps to 2 lines instead of forcing
        width (see the reference row further down) — belt and suspenders,
        since either alone would have masked the other's absence in review.
        The dialog also widens and switches to flex-column-with-internal-
        scroll only when the design section is present, so the ActionBar
        stays pinned and on screen (never off-screen at 1280×720) while a
        long reference list scrolls underneath it.
      */}
      <DialogContent
        className={cn(
          'flex max-h-[85vh] flex-col overflow-hidden',
          designFlagOn ? 'sm:max-w-2xl' : 'sm:max-w-lg',
        )}
      >
        <DialogHeader className="min-w-0 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <NotePencil size={16} />
            Project context
          </DialogTitle>
          <DialogDescription>
            What EDDY should keep in mind for this project — goals, constraints, references. He
            reads it on every run, and it stays with the project, so a later session picks it up
            too. Changes apply from his next run.
          </DialogDescription>
        </DialogHeader>

        {/* The one scrolling region — header and the Save/Revert bar below
            it are outside this div, so they never scroll out of view. */}
        <div className="min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto">
        <div>
          {isLoading ? (
            <p className="text-[length:var(--text-sm)] text-muted-foreground" style={{ margin: 0 }}>
              Loading…
            </p>
          ) : (
            <>
              {/* The failure gets its own branch: a loading test of `!data`
                  would read as "still loading" forever on an error, because a
                  failed query has no data AND is not fetching (CLAUDE.md
                  FRONT-1525). An unreadable file is still editable, so the
                  box stays usable underneath the warning. */}
              {(isError || unreadable) && (
                <p
                  className="text-[length:var(--text-sm)] text-destructive [text-wrap:pretty]"
                  style={{ margin: '0 0 8px' }}
                >
                  {unreadable
                    ? "Couldn't read this project's context just now, so saving is off — otherwise you'd overwrite something you can't see. Close this and reopen in a moment."
                    : `Could not read the current context: ${(error as Error)?.message ?? 'unknown error'}`}
                </p>
              )}
              <Textarea
                value={value}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={CODING_CONTEXT_MAX_CHARS}
                rows={8}
                aria-label="Project context"
                placeholder="What are you building, who is it for, anything it must or must not do."
                readOnly={unreadable}
                disabled={setContext.isPending}
                className="rounded-md shadow-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
              />
              <p
                className="text-[length:var(--text-xs)] text-muted-foreground tabular-nums"
                style={{ margin: '6px 0 0' }}
              >
                {value.trim().length === 0
                  ? 'Empty — saving removes the file from the project.'
                  : `${value.length.toLocaleString()} of ${CODING_CONTEXT_MAX_CHARS.toLocaleString()} characters`}
              </p>
            </>
          )}
        </div>

        {/* FRONT-2382: same section the New-project form folds under
            "Design direction · Optional" — here always expanded, since
            editing it IS the reason this dialog was opened for that
            project. Own top border rather than another `Optional` label:
            this whole block is already the "extra" one. Rendering,
            draft/save wiring live in design-direction-editor.tsx. */}
        {designFlagOn && <DesignDirectionEditor editor={designEditor} />}
        </div>

        <DesignDirectionEditorViewer reference={designEditor.viewerRef} onClose={designEditor.onCloseViewer} />

        <div className="-mx-4 -mb-4 shrink-0 min-w-0 overflow-hidden rounded-b-xl">
          <ActionBar
            dirty={dirty || designDirty}
            saving={setContext.isPending || designEditor.pending}
            onSave={handleSave}
            onDiscard={() => {
              setDraft(null);
              designEditor.discard();
            }}
            discardLabel="Revert"
            // "Revert" discards THIS unsaved edit; it is not an undo for a
            // completed save. So when the pending save is a deletion, the
            // button says so rather than calling it "Save". `willClear`
            // takes precedence over `designDirty` in `contextSaveCopy` —
            // clearing the context stays visible even when the design
            // section is ALSO dirty (FRONT-2382 review #11/#12: this used
            // to flip to the generic "Save changes" the moment BOTH were
            // dirty, hiding a destructive clear behind ordinary wording).
            {...contextSaveCopy({ willClear, designDirty })}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

